/**
 * Facility booking / membership events (America/Halifax).
 * Keep in sync with backend/src/utils/facilityEvents.ts
 *
 * Add a future event by appending to FACILITY_EVENTS. Call sites use the helpers
 * below — they should not hard-code event names or dates.
 *
 * availability:
 * - "from_start" (default): offer applies only once today is in the window,
 *   and only for slot dates in the window.
 * - "immediate": offer applies as soon as you book a slot in the window,
 *   including advance bookings. Other days still do not get the offer.
 */

export const FACILITY_TZ = "America/Halifax";

export const DEFAULT_REGISTRATION_FEE_DOLLARS = 20;

export type FacilityEventAvailability = "immediate" | "from_start";

export type FacilityEvent = {
  id: string;
  name: string;
  startYmd: string;
  endYmd: string;
  bookingLabel: string;
  membershipLabel?: string;
  waiveBookingTax?: boolean;
  bookingPercentOff?: number;
  stripeCouponId?: string;
  /** Percent off the membership price while this event is live. First payment only. */
  membershipPercentOff?: number;
  /** Stripe coupon id for membershipPercentOff. Separate from the booking coupon. */
  membershipStripeCouponId?: string;
  waiveRegistrationFee?: boolean;
  /** Default "from_start". Use "immediate" to allow booking the event dates early. */
  availability?: FacilityEventAvailability;
};

/**
 * Edit this list to add, extend, or retire events.
 */

// This affects booking and membership pricing
export const FACILITY_EVENTS: FacilityEvent[] = [
  {
    id: "anniversary-2026",
    name: "Anniversary Sale",
    startYmd: "2026-08-01",
    endYmd: "2026-08-31",
    bookingLabel: "Anniversary: 25% off facility bookings in August",
    membershipLabel: "Anniversary: $20 registration fee waived",
    bookingPercentOff: 25,
    stripeCouponId: "ANNIVERSARY25",
    waiveRegistrationFee: false,
    availability: "from_start",
  },
  {
    id: "labour-day-2026",
    name: "Labour Day",
    startYmd: "2026-09-07",
    endYmd: "2026-09-07",
    bookingLabel: "Labour Day: no taxes on bookings",
    waiveBookingTax: true,
    availability: "immediate",
  },
  {
    id: "halloween-2026",
    name: "Halloween Sale",
    startYmd: "2026-10-05",
    endYmd: "2026-10-31",
    bookingLabel: "Halloween: 25% off facility bookings",
    membershipLabel: "Halloween: 25% off membership and no registration fee",
    bookingPercentOff: 25,
    stripeCouponId: "HALLOWEEN25",
    membershipPercentOff: 25,
    membershipStripeCouponId: "HALLOWEEN25_MEM",
    waiveRegistrationFee: true,
    availability: "immediate",
  },
];

export function todayYmd(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: FACILITY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

export function toDateOnlyYmd(
  value: string | Date | null | undefined
): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    if (match) return match[1];
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return todayYmd(parsed);
  }
  if (Number.isNaN(value.getTime())) return null;
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function ymdInRange(
  ymd: string,
  startYmd: string,
  endYmd: string
): boolean {
  return ymd >= startYmd && ymd <= endYmd;
}

export function getEventAvailability(
  event: FacilityEvent
): FacilityEventAvailability {
  return event.availability ?? "from_start";
}

export function eventsLiveNow(now: Date = new Date()): FacilityEvent[] {
  const today = todayYmd(now);
  return FACILITY_EVENTS.filter((event) =>
    ymdInRange(today, event.startYmd, event.endYmd)
  );
}

function eventQualifiesForSlot(
  event: FacilityEvent,
  slot: string,
  today: string
): boolean {
  if (!ymdInRange(slot, event.startYmd, event.endYmd)) return false;
  if (today > event.endYmd) return false;
  if (getEventAvailability(event) === "immediate") return true;
  return ymdInRange(today, event.startYmd, event.endYmd);
}

/** Events that apply to this checkout for the selected slot date. */
export function matchingBookingEvents(
  bookingForDate?: string | Date | null,
  now: Date = new Date()
): FacilityEvent[] {
  const slot = toDateOnlyYmd(bookingForDate);
  if (!slot) return [];
  const today = todayYmd(now);
  return FACILITY_EVENTS.filter((event) =>
    eventQualifiesForSlot(event, slot, today)
  );
}

export function shouldWaiveBookingTax(
  bookingForDate: string | Date | null | undefined,
  now: Date = new Date()
): boolean {
  return matchingBookingEvents(bookingForDate, now).some(
    (event) => event.waiveBookingTax
  );
}

export function getBookingPercentOff(
  bookingForDate: string | Date | null | undefined,
  now: Date = new Date()
): { percent: number; couponId?: string; event?: FacilityEvent } {
  const discounted = matchingBookingEvents(bookingForDate, now)
    .filter((event) => (event.bookingPercentOff ?? 0) > 0)
    .sort(
      (a, b) => (b.bookingPercentOff ?? 0) - (a.bookingPercentOff ?? 0)
    );
  const best = discounted[0];
  return {
    percent: best?.bookingPercentOff ?? 0,
    couponId: best?.stripeCouponId,
    event: best,
  };
}

export function shouldWaiveRegistrationFee(now: Date = new Date()): boolean {
  return eventsLiveNow(now).some((event) => event.waiveRegistrationFee);
}

/** Highest membership percent-off among events live today. */
export function getMembershipPercentOff(
  now: Date = new Date()
): { percent: number; couponId?: string; event?: FacilityEvent } {
  const discounted = eventsLiveNow(now)
    .filter((event) => (event.membershipPercentOff ?? 0) > 0)
    .sort(
      (a, b) => (b.membershipPercentOff ?? 0) - (a.membershipPercentOff ?? 0)
    );
  const best = discounted[0];
  return {
    percent: best?.membershipPercentOff ?? 0,
    couponId: best?.membershipStripeCouponId,
    event: best,
  };
}

export function getActiveMembershipEvents(now: Date = new Date()): FacilityEvent[] {
  return eventsLiveNow(now).filter(
    (event) =>
      event.waiveRegistrationFee ||
      (event.membershipPercentOff ?? 0) > 0 ||
      event.membershipLabel
  );
}

/** When offers overlap, keep the highest booking percent and any non-percent offers. */
function selectDisplayedBookingEvents(events: FacilityEvent[]): FacilityEvent[] {
  const bestPercent = events.reduce(
    (max, event) => Math.max(max, event.bookingPercentOff ?? 0),
    0
  );
  if (bestPercent <= 0) return events;
  return events.filter((event) => {
    const percent = event.bookingPercentOff ?? 0;
    return percent <= 0 || percent === bestPercent;
  });
}

export function getBookingEventNotices(
  bookingForDate: string | Date | null | undefined,
  now: Date = new Date()
): FacilityEvent[] {
  return selectDisplayedBookingEvents(matchingBookingEvents(bookingForDate, now));
}

export function formatEventDateRange(event: FacilityEvent): string {
  const start = formatYmdLabel(event.startYmd);
  if (event.startYmd === event.endYmd) return start;
  return `${start} – ${formatYmdLabel(event.endYmd)}`;
}

function formatYmdLabel(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function hasBookingOffer(event: FacilityEvent): boolean {
  return Boolean(event.waiveBookingTax || (event.bookingPercentOff ?? 0) > 0);
}

/** Live or upcoming booking offers (expired events are hidden). */
export function getPublicBookingOffers(
  now: Date = new Date()
): Array<FacilityEvent & { live: boolean }> {
  const today = todayYmd(now);
  return FACILITY_EVENTS.filter(
    (event) => hasBookingOffer(event) && event.endYmd >= today
  ).map((event) => ({
    ...event,
    live: ymdInRange(today, event.startYmd, event.endYmd),
  }));
}

export function getBookingOfferHeadline(
  event: FacilityEvent & { live?: boolean }
): string {
  const when = event.live ? "today" : formatEventDateRange(event);
  if ((event.bookingPercentOff ?? 0) >= 100) {
    return `${event.name} (${when}): bookings are free`;
  }
  if (event.waiveBookingTax) {
    return `${event.name} (${when}): tax waived — $0 tax on same-day bookings`;
  }
  if ((event.bookingPercentOff ?? 0) > 0) {
    return `${event.name} (${when}): ${event.bookingPercentOff}% off facility bookings`;
  }
  return `${event.name} (${when}): ${event.bookingLabel}`;
}

export function ymdToLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function getEventCalendarDates(event: FacilityEvent): Date[] {
  const dates: Date[] = [];
  const cursor = ymdToLocalDate(event.startYmd);
  const end = ymdToLocalDate(event.endYmd);
  while (cursor <= end) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export function getOfferCalendarDates(now: Date = new Date()): Date[] {
  return getPublicBookingOffers(now).flatMap(getEventCalendarDates);
}

export function getOffersCoveringDate(
  bookingForDate: string | Date | null | undefined,
  now: Date = new Date()
): Array<FacilityEvent & { live: boolean }> {
  const ymd = toDateOnlyYmd(bookingForDate);
  if (!ymd) return [];
  return getPublicBookingOffers(now).filter((event) =>
    ymdInRange(ymd, event.startYmd, event.endYmd)
  );
}

export function applyFacilityBookingPricing(
  subtotal: number,
  bookingForDate?: string | Date | null,
  now: Date = new Date()
): {
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  total: number;
  promoActive: boolean;
  taxWaived: boolean;
  notices: FacilityEvent[];
} {
  const { percent } = getBookingPercentOff(bookingForDate, now);
  const discountAmount =
    Math.round(subtotal * (percent / 100) * 100) / 100;
  const total = Math.round((subtotal - discountAmount) * 100) / 100;

  return {
    subtotal,
    discountPercent: percent,
    discountAmount,
    total,
    promoActive: percent > 0,
    taxWaived: shouldWaiveBookingTax(bookingForDate, now),
    notices: getBookingEventNotices(bookingForDate, now),
  };
}

/** Membership price after the live event percent-off. Same shape as booking pricing. */
export function applyFacilityMembershipPricing(
  amount: number,
  now: Date = new Date()
): {
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  total: number;
  promoActive: boolean;
  event?: FacilityEvent;
} {
  const { percent, event } = getMembershipPercentOff(now);
  const discountAmount = Math.round(amount * (percent / 100) * 100) / 100;
  const total = Math.round((amount - discountAmount) * 100) / 100;

  return {
    subtotal: amount,
    discountPercent: percent,
    discountAmount,
    total,
    promoActive: percent > 0,
    event,
  };
}
